/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { useMergeRefs } from '@floating-ui/react';
import {
  type Cell,
  type CellContext,
  flexRender,
  type Header as HeaderType,
  type Row,
  type RowData,
  type SortDirection,
} from '@tanstack/react-table';
import classNames from 'classnames';
import type React from 'react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import {
  Bars3CenterLeftIconOutline,
  BarsArrowDownIconOutline,
  BarsArrowUpIconOutline,
  ChevronDownIconSolid,
  ChevronLeftIconOutline,
  ChevronRightIconOutline,
  DataGridCrossIcon,
  EllipsisHorizontalIconOutline,
} from '../icons';
import { LoadingSpinner } from '../loading-spinner';
import { Menu } from '../menu';
import { Select } from '../select';
import { Skeleton } from '../skeleton';
import { Typography } from '../typography';
import { useDataGridContext } from './data-grid-context';
import { type ColumnAction } from './datagrid-types';
import {
  resizeColumn,
  ResizeDirection,
  userFriendlyPagination,
} from './helpers';

/** Table results per page, with the first option being the default */
const TABLE_RESULTS = {
  DEFAULT: 10,
  values: [10, 25, 50, 100],
} as const;

type paginationOption = {
  value: number;
  label: string;
};

const getPaginationOptions = (pageSize: number): paginationOption[] => {
  return [...new Set([...TABLE_RESULTS.values, pageSize])]
    .sort((a, b) => a - b)
    .map((option) => ({
      label: `${option}`,
      value: option,
    }));
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const DEFAULT_SORT_ACTIONS: (cell: HeaderType<any, unknown>) => {
  [key in SortDirection | 'default']: ColumnAction;
} = (cell) => ({
  asc: {
    icon: <BarsArrowUpIconOutline />,
    onClick: () => cell.column.toggleSorting(false),
    title: 'Sort ascending',
  },
  default: {
    icon: <Bars3CenterLeftIconOutline />,
    onClick: () => cell.column.clearSorting(),
    title: 'Default sort',
  },
  desc: {
    icon: <BarsArrowDownIconOutline />,
    onClick: () => cell.column.toggleSorting(true),
    title: 'Sort descending',
  },
});

type ResizingBarProps<T extends RowData> = {
  header: HeaderType<T, unknown>;
};

const ResizingBar = <T extends RowData>({
  header,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'button', ResizingBarProps<T>>) => {
  const { isKeyboardNavigationEnabled } = useDataGridContext<T>();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowLeft') {
      event.stopPropagation();
      event.preventDefault();
      resizeColumn(ResizeDirection.LEFT, header);
    } else if (event.key === 'ArrowRight') {
      event.stopPropagation();
      event.preventDefault();
      resizeColumn(ResizeDirection.RIGHT, header);
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      className={classNames('ndl-data-grid-resizer', className, {
        'ndl-data-grid-is-resizing': header.column.getIsResizing(),
      })}
      style={style}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onKeyDown={handleKeyDown}
      aria-label="Resizing bar"
      tabIndex={isKeyboardNavigationEnabled ? -1 : 0}
      {...restProps}
      {...htmlAttributes}
    />
  );
};

type ScrollableProps = {
  children?: React.ReactNode;
};

const Scrollable = ({
  children,
  ref,
  className,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', ScrollableProps> & {
  ref: React.RefObject<HTMLDivElement | null>;
}) => {
  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-data-grid-scrollable', className)}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

type HeaderProps = {
  children?: React.ReactNode;
};

const Header = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', HeaderProps>) => {
  const { tableProps, components } = useDataGridContext();
  const { getHeaderGroups, getState } = tableProps;

  return (
    <div
      role="rowgroup"
      className={classNames('ndl-data-grid-thead', className, {
        'ndl-data-grid-is-resizing':
          getState().columnSizingInfo.isResizingColumn,
      })}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          {getHeaderGroups().map((headerGroup) => (
            <div key={headerGroup.id} className="ndl-data-grid-tr" role="row">
              {headerGroup.headers.map(
                (header) =>
                  components.HeaderCell && (
                    <components.HeaderCell key={header.id} cell={header} />
                  ),
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

type ActionButtonProps = {
  action: ColumnAction;
  onClose: () => void;
};

export const ActionButton = <T extends React.ElementType = 'button'>({
  action,
  onClose,
  as,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, ActionButtonProps>) => {
  const { onClick, ...restAction } = action;

  const interceptedOnClick = useCallback(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const providedOnClick = onClick ?? action.onClick;
      if (typeof providedOnClick === 'function') {
        providedOnClick(e);
      }
      onClose?.();
    },
    [action.onClick, onClick, onClose],
  );

  return (
    <Menu.Item
      onClick={interceptedOnClick}
      as={as}
      className={className}
      style={style}
      htmlAttributes={htmlAttributes}
      ref={ref}
      {...restAction}
      {...restProps}
    />
  );
};

export interface ActionCellProps<T extends RowData> {
  cell?: CellContext<T, unknown>;
  innerCleanIconButtonProps?: Partial<
    React.ComponentPropsWithoutRef<typeof CleanIconButton>
  >;
  innerMenuProps?: Partial<React.ComponentProps<typeof Menu>>;
}

const ActionCell = <T extends RowData>({
  cell,
  innerCleanIconButtonProps,
  innerMenuProps,
}: ActionCellProps<T>) => {
  const { components, isSkeletonLoading, portalTarget } =
    useDataGridContext<T>();
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  if (
    !cell ||
    !cell.column.columnDef.meta ||
    typeof cell.column.columnDef?.meta?.isActionCell !== 'object'
  ) {
    return null;
  }

  const { actions, onOpenChange } = cell.column.columnDef.meta.isActionCell;

  if (isSkeletonLoading) {
    return null;
  }

  return (
    <>
      <CleanIconButton
        as="button"
        size="medium"
        description="Actions"
        ref={actionsButtonRef}
        isActive={isActionsOpen}
        onClick={() => {
          onOpenChange?.(!isActionsOpen);
          setIsActionsOpen(!isActionsOpen);
        }}
        {...innerCleanIconButtonProps}
        htmlAttributes={{
          tabIndex: 0,
          ...innerCleanIconButtonProps?.htmlAttributes,
        }}
      >
        {innerCleanIconButtonProps?.children || (
          <EllipsisHorizontalIconOutline />
        )}
      </CleanIconButton>
      <Menu
        isOpen={isActionsOpen}
        anchorRef={actionsButtonRef}
        onClose={() => {
          onOpenChange?.(false);
          setIsActionsOpen(false);
        }}
        {...innerMenuProps}
        portalTarget={portalTarget}
      >
        {innerMenuProps?.children || (
          <>
            {actions.map(
              (action, i) =>
                components.ActionButton && (
                  <components.ActionButton
                    key={`${cell.column.id}-action-${i}`}
                    action={{
                      ...action,
                      onClick: (e) => {
                        action.onClick?.(e, cell);
                      },
                    }}
                    onClose={() => setIsActionsOpen(false)}
                  />
                ),
            )}
          </>
        )}
      </Menu>
    </>
  );
};

type ColumnControlsProps<T extends RowData> = {
  cell: HeaderType<T, unknown>;
  children?: React.ReactNode;
};

export const ColumnControls = <T extends RowData>({
  cell,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', ColumnControlsProps<T>>) => {
  const { components, portalTarget } = useDataGridContext();

  const sort = cell.column.getIsSorted();
  const canSort = cell.column.getCanSort();
  const { hasDefaultSortingActions = true, actions } =
    cell.column.columnDef.meta?.columnActions || {};

  const sortingActions = useMemo(() => {
    if (canSort) {
      if (sort !== 'asc' && sort !== 'desc') {
        return [
          DEFAULT_SORT_ACTIONS(cell).asc,
          DEFAULT_SORT_ACTIONS(cell).desc,
        ];
      }
      if (sort === 'asc') {
        return [
          DEFAULT_SORT_ACTIONS(cell).default,
          DEFAULT_SORT_ACTIONS(cell).desc,
        ];
      }
      if (sort === 'desc') {
        return [
          DEFAULT_SORT_ACTIONS(cell).default,
          DEFAULT_SORT_ACTIONS(cell).asc,
        ];
      }
    }
    // Default case
    return [];
  }, [sort, cell, canSort]);

  const newActions: ColumnAction[] = [
    ...(hasDefaultSortingActions ? sortingActions : []),
    ...(actions ?? []),
  ];

  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  if (!actions || actions.length === 0) {
    return null;
  }

  return newActions.length > 0 ? (
    <div
      ref={ref}
      className={classNames('ndl-header-action-group', className)}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <Fragment>
          <CleanIconButton
            size="small"
            description="Actions"
            ref={actionsButtonRef}
            isActive={isActionsOpen}
            onClick={() => {
              setIsActionsOpen(!isActionsOpen);
            }}
            htmlAttributes={{
              tabIndex: 0,
            }}
          >
            <ChevronDownIconSolid />
          </CleanIconButton>
          <Menu
            isOpen={isActionsOpen}
            anchorRef={actionsButtonRef}
            onClose={() => setIsActionsOpen(false)}
            portalTarget={portalTarget}
          >
            {newActions.map(
              (action, i) =>
                components.ActionButton && (
                  <components.ActionButton
                    key={`${cell.column.id}-action-${i}`}
                    action={action}
                    onClose={() => setIsActionsOpen(false)}
                  />
                ),
            )}
          </Menu>
        </Fragment>
      )}
    </div>
  ) : null;
};

type HeaderCellProps<T> = {
  cell: HeaderType<T, unknown>;
  children?: React.ReactNode;
};

const HeaderCell = <T extends RowData>({
  cell,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', HeaderCellProps<T>>) => {
  const { components, isResizable, isKeyboardNavigationEnabled } =
    useDataGridContext<T>();

  const sort = cell.column.getIsSorted();
  const canSort = cell.column.getCanSort();

  const ariaSortRole = useMemo(() => {
    switch (sort) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  }, [sort]);

  const Icon = useMemo(() => {
    switch (sort) {
      case 'asc':
        return <BarsArrowUpIconOutline className="ndl-header-icon" />;
      case 'desc':
        return <BarsArrowDownIconOutline className="ndl-header-icon" />;
      default:
        return (
          <Bars3CenterLeftIconOutline className="ndl-hoverable-indicator ndl-header-icon" />
        );
    }
  }, [sort]);

  const ariaDescription = useMemo(() => {
    return canSort
      ? `Press ENTER or SPACE to sort column ${cell.column.id}`
      : `Column id: ${cell.column.id}`;
  }, [canSort, cell.column.id]);

  const isActionColumn = useMemo(
    () => typeof cell.column.columnDef.meta?.isActionCell === 'object',
    [cell.column.columnDef.meta],
  );

  const Tag = canSort ? 'button' : 'div';

  const InnerHeaderCell = () => (
    <div className="ndl-header-cell">
      {cell.isPlaceholder
        ? null
        : flexRender(cell.column.columnDef.header, cell.getContext())}
      {canSort && Icon}
    </div>
  );

  return (
    <div
      ref={ref}
      tabIndex={isKeyboardNavigationEnabled && !isActionColumn ? 0 : undefined}
      key={cell.id}
      role="columnheader"
      className={classNames('ndl-data-grid-th', className, {
        'ndl-data-grid-is-resizing': cell.column.getIsResizing(),
        'ndl-data-grid-pinned-cell': cell.column.getIsPinned(),
        'ndl-data-grid-pinned-cell-left': cell.column.getIsPinned() === 'left',
        'ndl-data-grid-pinned-cell-right':
          cell.column.getIsPinned() === 'right',
        'ndl-data-grid-row-action': isActionColumn,
        'ndl-focusable-cell': isKeyboardNavigationEnabled,
      })}
      aria-sort={ariaSortRole}
      style={{
        ...style,
        maxWidth: cell.column.columnDef.maxSize
          ? `${cell.column.columnDef.maxSize}px`
          : 'none',
        width: isActionColumn ? 40 : cell.getSize(),
      }}
      {...(isKeyboardNavigationEnabled
        ? {
            onKeyDown: (e) => {
              if (
                isResizable &&
                e.altKey &&
                (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
              ) {
                const direction =
                  e.key === 'ArrowLeft'
                    ? ResizeDirection.LEFT
                    : ResizeDirection.RIGHT;
                resizeColumn(direction, cell);
                e.preventDefault();
                e.stopPropagation();
                return;
              }
            },
          }
        : {})}
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          <Tag
            className="ndl-header-group"
            style={{
              cursor: canSort ? 'pointer' : 'default',
            }}
            aria-description={ariaDescription}
            onClick={() => canSort && cell.column.toggleSorting()}
            tabIndex={canSort ? 0 : undefined}
          >
            {components.HeaderTitle ? (
              <components.HeaderTitle cell={cell}>
                <InnerHeaderCell />
              </components.HeaderTitle>
            ) : (
              <InnerHeaderCell />
            )}
          </Tag>
          {/* Column actions*/}
          {components.ColumnControls && (
            <components.ColumnControls cell={cell} />
          )}
          {/* Resizer*/}
          {isResizable &&
            cell.column.getCanResize() &&
            components.ResizingBar && <components.ResizingBar header={cell} />}
        </>
      )}
    </div>
  );
};

type HeaderTitleProps<T> = {
  cell: HeaderType<T, unknown>;
  children?: React.ReactNode;
};

const HeaderTitle = <T extends RowData>({
  cell,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', HeaderTitleProps<T>>) => {
  const { column, id } = cell;
  const title =
    typeof column.columnDef.header === 'string' ? column.columnDef.header : id;
  return (
    <div
      ref={ref}
      className={className}
      title={title}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

type BodyProps = {
  children?: React.ReactNode;
};

const Body = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', BodyProps>) => {
  const { tableProps, components, isLoading } = useDataGridContext();
  const { getRowModel } = tableProps;

  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-data-grid-tbody', className)}
      role="rowgroup"
      aria-busy={isLoading ? 'true' : 'false'}
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          {isLoading && components.LoadingPlaceholder && (
            <components.LoadingPlaceholder />
          )}

          {!isLoading &&
            getRowModel().rows.length === 0 &&
            components.NoDataPlaceholder && <components.NoDataPlaceholder />}

          {!isLoading &&
            getRowModel().rows.map(
              (row, idx) =>
                components.BodyRow && (
                  <components.BodyRow key={`table-row-${idx}`} row={row} />
                ),
            )}
        </>
      )}
    </div>
  );
};

type BodyCellProps<T extends RowData> = {
  cell: Cell<T, unknown>;
  children?: React.ReactNode;
};

const BodyCell = <T extends RowData>({
  cell,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', BodyCellProps<T>>) => {
  const isResizing = cell.column.getIsResizing();

  const isActionColumn = Boolean(cell.column.columnDef.meta?.isActionCell);
  const isInlineEditCell = Boolean(
    cell.column.columnDef.meta?.isInlineEditCell,
  );
  const isDropDownCell = Boolean(cell.column.columnDef.meta?.isDropDownCell);
  const isCustomCell = isActionColumn || isDropDownCell;
  const { isKeyboardNavigationEnabled, isSkeletonLoading, skeletonProps } =
    useDataGridContext();
  return (
    <div
      ref={ref}
      key={cell.id}
      className={classNames('ndl-data-grid-td', className, {
        'ndl-data-grid-custom-cell': isCustomCell,
        'ndl-data-grid-dropdown-cell': isDropDownCell,
        'ndl-data-grid-inline-edit': isInlineEditCell,
        'ndl-data-grid-is-resizing': isResizing,
        'ndl-data-grid-pinned-cell': cell.column.getIsPinned(),
        'ndl-data-grid-pinned-cell-left': cell.column.getIsPinned() === 'left',
        'ndl-data-grid-pinned-cell-right':
          cell.column.getIsPinned() === 'right',
        'ndl-data-grid-row-action': isActionColumn,
        'ndl-focusable-cell': isKeyboardNavigationEnabled,
      })}
      role="cell"
      tabIndex={isKeyboardNavigationEnabled ? 0 : undefined}
      style={{
        ...style,
        width: isActionColumn ? 40 : cell.column.getSize(),
      }}
      {...restProps}
      {...htmlAttributes}
    >
      <Skeleton
        onBackground="weak"
        shape="rectangular"
        width="100%"
        {...skeletonProps}
        isLoading={isSkeletonLoading && !isActionColumn}
      >
        {children || flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Skeleton>
    </div>
  );
};

type BodyRowProps<T extends RowData> = {
  row: Row<T>;
  children?: React.ReactNode;
};

const BodyRow = <T extends RowData>({
  row,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', BodyRowProps<T>>) => {
  const { components } = useDataGridContext<T>();

  return (
    <div
      key={row.id}
      ref={ref}
      style={style}
      className={classNames('ndl-data-grid-tr', className)}
      role="row"
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          {row
            .getVisibleCells()
            .map(
              (cell) =>
                components.BodyCell && (
                  <components.BodyCell key={cell.id} cell={cell} />
                ),
            )}
        </>
      )}
    </div>
  );
};

type TableResultProps = {
  manualPagination?: {
    from: number;
    to: number;
    totalRows: number;
  };
  children?: React.ReactNode;
  /**
   * The label to use for the results, meaning the word "results" in the sentence "Showing X-Y of Z results".
   * @defaultValue 'results'
   */
  resultsLabel?: string;
};

const TableResults = ({
  manualPagination,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  resultsLabel = 'results',
  ...restProps
}: CommonProps<'span', TableResultProps>) => {
  const { tableProps, isSkeletonLoading, skeletonProps, isSmallNavigation } =
    useDataGridContext();
  const { getState, getRowModel, getCoreRowModel } = tableProps;
  const {
    pagination: { pageSize, pageIndex },
  } = getState();
  const { rows } = getRowModel();

  const { from, to, totalRows } = useMemo(
    () =>
      manualPagination || {
        from: 1 + pageIndex * pageSize,
        to: rows.length + pageIndex * pageSize,
        totalRows: getCoreRowModel().rows.length,
      },
    [pageIndex, pageSize, manualPagination, rows, getCoreRowModel],
  );

  if (isSmallNavigation) {
    return null;
  }

  return (
    <Skeleton
      as="span"
      onBackground="weak"
      shape="rectangular"
      {...skeletonProps}
      isLoading={isSkeletonLoading}
    >
      <span
        ref={ref}
        style={style}
        className={classNames('ndl-data-grid-results', className)}
        {...restProps}
        {...htmlAttributes}
      >
        {children || (
          <span>
            Showing{' '}
            <span className="n-font-bold">
              {totalRows ? `${from}${to !== from ? `-${to}` : ''}` : 0}
            </span>{' '}
            of <span className="n-font-bold">{totalRows}</span> {resultsLabel}
          </span>
        )}
      </span>
    </Skeleton>
  );
};

type RowsPerPageProps = {
  /**
   * The target element to portal the menu of the select component to.
   * Should be document.body normally, or the modal root if the select is inside a modal.
   * Defaults to the portalTarget prop of the DataGrid root component.
   */
  portalTarget?: HTMLElement | null;
  children?: React.ReactNode;
};

const RowsPerPage = ({
  portalTarget: portalTargetProp,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', RowsPerPageProps>) => {
  const { tableProps, portalTarget } = useDataGridContext();

  const { setPageSize, getState } = tableProps;

  const {
    pagination: { pageSize },
  } = getState();

  const paginationOptions = getPaginationOptions(pageSize);
  const defaultPaginationOption = paginationOptions.find(
    (option) => option.value === pageSize,
  );

  const menuPortalTarget =
    portalTargetProp === undefined ? portalTarget : portalTargetProp;

  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-data-grid-rows-per-page', className)}
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          Show
          <Select
            type="select"
            size="medium"
            ariaLabel="Select page size"
            selectProps={{
              defaultValue: defaultPaginationOption,
              isSearchable: false,
              menuPortalTarget: menuPortalTarget,
              menuPosition: 'fixed',
              onChange: (option) => {
                if (option) {
                  setPageSize(option.value);
                }
              },
              options: paginationOptions,
              styles: {
                container: () => ({
                  width: '100%',
                }),
              },
            }}
          />
        </>
      )}
    </div>
  );
};

type PaginationArrowButtonProps = {
  action: 'previous' | 'next';
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const PaginationArrowButton = ({
  action,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'button', PaginationArrowButtonProps>) => {
  const PaginationIcon =
    action === 'previous' ? ChevronLeftIconOutline : ChevronRightIconOutline;
  const ariaLabel = action === 'previous' ? 'Previous page' : 'Next page';
  const classes = classNames('ndl-data-grid-pagination-icon-button', className);

  return (
    <CleanIconButton
      ref={ref}
      style={style}
      className={classes}
      description={ariaLabel}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      {children || <PaginationIcon />}
    </CleanIconButton>
  );
};

type PaginationNumericButtonProps = {
  currentIndex: number | string;
  isSelected: boolean;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const PaginationNumericButton = ({
  currentIndex,
  isSelected,
  children,
  className,
  style,
  htmlAttributes,
  onClick,
  ref,
  ...restProps
}: CommonProps<'button', PaginationNumericButtonProps>) => {
  return (
    <button
      ref={ref}
      style={style}
      type="button"
      data-testid={`ndl-table-${currentIndex}`}
      className={classNames(
        'ndl-data-grid-pagination-numeric-button',
        className,
        {
          'ndl-is-selected': isSelected,
          'ndl-not-selected': !isSelected,
          'ndl-not-selected-numeric':
            typeof currentIndex === 'number' && !isSelected,
        },
      )}
      onClick={(e) => typeof currentIndex === 'number' && onClick && onClick(e)}
      tabIndex={typeof currentIndex === 'number' ? 0 : -1}
      {...restProps}
      {...htmlAttributes}
    >
      {children || currentIndex}
    </button>
  );
};

const PaginationNumericButtons = () => {
  const { tableProps, components } = useDataGridContext();
  const { getState, setPageIndex, getPageCount } = tableProps;
  const {
    pagination: { pageIndex },
  } = getState();

  return (
    <>
      {userFriendlyPagination(pageIndex + 1, getPageCount()).map(
        (option, idx) =>
          components.PaginationNumericButton && (
            <components.PaginationNumericButton
              /** Use index to avoid situations where two "..." values will appear, causing a key conflict */
              key={`${option}-${idx}`}
              onClick={() =>
                typeof option === 'number' && setPageIndex(option - 1)
              }
              currentIndex={option}
              isSelected={option === pageIndex + 1}
            />
          ),
      )}
    </>
  );
};

type PaginationProps = {
  children?: React.ReactNode;
};

const Pagination = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'nav', PaginationProps>) => {
  const { tableProps, components, isMediumNavigation } = useDataGridContext();

  const { getCanPreviousPage, getCanNextPage, previousPage, nextPage } =
    tableProps;

  return (
    <nav
      ref={ref}
      style={style}
      className={classNames('ndl-data-grid-nav', className)}
      aria-label="Pagination"
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <>
          {/* Previous page */}
          {getCanPreviousPage()
            ? components.PaginationArrowButton && (
                <components.PaginationArrowButton
                  action="previous"
                  onClick={previousPage}
                />
              )
            : null}
          {/* Pages - hidden on narrow containers */}
          {!isMediumNavigation && components.PaginationNumericButtons && (
            <components.PaginationNumericButtons />
          )}
          {/* Next page */}
          {getCanNextPage()
            ? components.PaginationArrowButton && (
                <components.PaginationArrowButton
                  action="next"
                  onClick={nextPage}
                />
              )
            : null}
        </>
      )}
    </nav>
  );
};

type NavigationProps = {
  children?: React.ReactNode;
};

const Navigation = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', NavigationProps>) => {
  const {
    tableProps,
    components,
    isSkeletonLoading,
    skeletonProps,
    isSmallNavigation,
  } = useDataGridContext();

  const { getPageCount } = tableProps;

  return (
    <div
      ref={ref}
      style={{
        ...style,
        ...(isSmallNavigation && { justifyContent: 'end' }),
      }}
      className={classNames('ndl-data-grid-navigation', className)}
      {...restProps}
      {...htmlAttributes}
    >
      <>
        {children || (
          <>
            {components.TableResults && <components.TableResults />}
            <Skeleton
              onBackground="weak"
              shape="rectangular"
              height="36px"
              {...skeletonProps}
              isLoading={isSkeletonLoading}
            >
              <div className="ndl-data-grid-navigation-right-items">
                {getPageCount() > 1 && components.Pagination && (
                  <components.Pagination />
                )}
                {/* Page size selector */}
                {components.RowsPerPage && <components.RowsPerPage />}
              </div>
            </Skeleton>
          </>
        )}
      </>
    </div>
  );
};

type LoadingPlaceholderProps = {
  children?: React.ReactNode;
};

const LoadingPlaceholder = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', LoadingPlaceholderProps>) => {
  return (
    <div
      ref={ref}
      style={style}
      className={classNames('nld-table-placeholder-wrapper', className)}
      role="row"
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <div role="cell" className="ndl-data-grid-placeholder">
          <div className="ndl-data-grid-loading-placeholder">
            <LoadingSpinner />
            <h6>Loading data</h6>
          </div>
        </div>
      )}
    </div>
  );
};

type NoDataPlaceholderProps = {
  children?: React.ReactNode;
};

const NoDataPlaceholder = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', NoDataPlaceholderProps>) => {
  return (
    <div
      ref={ref}
      style={style}
      className={classNames('nld-table-placeholder-wrapper', className)}
      role="row"
      {...restProps}
      {...htmlAttributes}
    >
      {children || (
        <NoDataPlaceholderContentWrapper>
          <NoDataIcon />
          <Typography variant="subheading-small">No data available</Typography>
        </NoDataPlaceholderContentWrapper>
      )}
    </div>
  );
};

type NoDataPlaceholderContentWrapperProps = {
  children?: React.ReactNode;
};

export const NoDataPlaceholderContentWrapper = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', NoDataPlaceholderContentWrapperProps>) => {
  return (
    <div
      role="cell"
      className={classNames('ndl-data-grid-placeholder', className)}
      ref={ref}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

export interface InlineEditCellProps<T extends RowData> {
  cell: CellContext<T, unknown>;
  value: string;
  ariaLabel: string;
}

const InlineEditCell = <T extends RowData>({
  cell,
  value,
  ariaLabel,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'input', InlineEditCellProps<T>>) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const { dataGridNav } = useDataGridContext<T>();

  if (!cell.column.columnDef.meta?.isInlineEditCell) {
    return null;
  }

  return (
    <input
      ref={ref}
      style={style}
      className={className}
      onChange={(e) => setInputValue(e.target.value)}
      value={inputValue}
      onFocus={() => {
        dataGridNav.disable();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          cell?.column.columnDef.meta?.isInlineEditCell?.onEditChange(
            inputValue,
            cell,
          );
          e.currentTarget.parentElement?.focus();
          return;
        } else if (e.key === 'Escape') {
          e.currentTarget.parentElement?.focus();
          return;
        }
      }}
      onBlur={() => {
        setInputValue(value);
        dataGridNav.enable();
      }}
      aria-label={ariaLabel}
      {...restProps}
      {...htmlAttributes}
    />
  );
};

type DropDownCellProps<T extends RowData> = {
  cell: CellContext<T, unknown>;
  options: {
    value: string;
    label: string;
  }[];
  /**
   * The target element to portal the menu of the select component to, e.g. the modal root if the data grid is inside a modal.
   * Defaults to the portalTarget prop of the DataGrid root component.
   */
  portalTarget?: HTMLElement | null;
  /** Aria label of the select component. Required for accessibility. */
  ariaLabel: string;
  /** Whether the select component is disabled */
  isDisabled?: boolean;
};

const DropDownCell = <T extends RowData>({
  cell,
  options,
  portalTarget: portalTargetProp,
  ariaLabel,
  className,
  style,
  htmlAttributes,
  ref: forwardRef,
  isDisabled,
  ...restProps
}: CommonProps<'div', DropDownCellProps<T>>) => {
  const { dataGridNav, portalTarget } = useDataGridContext<T>();
  const ref = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([ref, forwardRef]);

  if (!cell.column.columnDef.meta?.isDropDownCell) {
    return null;
  }
  const defaultOption = options.find(
    (option) => option.value === cell.getValue()?.toString(),
  );

  const menuPortalTarget =
    portalTargetProp === undefined ? portalTarget : portalTargetProp;

  return (
    <Select
      ref={mergedRef}
      className={className}
      isFluid={true}
      isClean={true}
      style={{ ...style, height: '100%', width: '100%' }}
      type="select"
      size="medium"
      ariaLabel={ariaLabel}
      isDisabled={isDisabled}
      selectProps={{
        defaultValue: defaultOption,
        menuPortalTarget: menuPortalTarget,
        menuPosition: 'fixed',
        onChange: (e) => {
          const value = e?.value;
          cell?.column.columnDef.meta?.isDropDownCell?.onChange(
            value?.toString(),
            cell,
          );
          dataGridNav.enable();
          if (ref?.current) {
            dataGridNav.focusParentCell(ref.current);
          }
        },
        onMenuOpen: () => {
          dataGridNav.disable();
        },
        options: options,
        styles: {
          container: () => ({ height: '100%', width: '100%' }),
          control: () => ({
            borderRadius: 0,
            height: '100%',
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
          }),
        },
      }}
      htmlAttributes={htmlAttributes}
      {...restProps}
    />
  );
};

const NoDataIcon = ({
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof DataGridCrossIcon>) => {
  const classes = classNames('n-size-token-64', className);
  return <DataGridCrossIcon className={classes} {...restProps} />;
};

export {
  ResizingBar,
  Header,
  Body,
  BodyCell,
  BodyRow,
  InlineEditCell,
  DropDownCell,
  ActionCell,
  HeaderCell,
  HeaderTitle,
  Navigation,
  TableResults,
  RowsPerPage,
  Pagination,
  PaginationNumericButtons,
  PaginationArrowButton,
  PaginationNumericButton,
  NoDataPlaceholder,
  NoDataIcon,
  LoadingPlaceholder,
  Scrollable,
};
